<template>
  <div>
    <div class="subtitle2 mt-4">
      {{ $t("modals.addAccessPermissions.assignUsers.title") }}
    </div>

    <button-toggle-group
      v-model="localValue.userType"
      :items="userTypeItems"
      class="mt-4 mb-6 flex-grow-1 w-100"
      density="comfortable"
      bg-color="indigo-faint"
    />

    <v-autocomplete
      v-if="isSpecificGroup"
      v-model="localValue.groups"
      class="mt-6"
      :loading="dataLoading"
      :items="groupsOptions"
      :label="localValue.groups.length ? $t('modals.addAccessPermissions.group.label') : ''"
      :placeholder="$t('modals.addAccessPermissions.group.placeholder')"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      multiple
      no-filter
      return-object
      :hide-no-data="!groupSearchTerm?.length"
      :no-data-text="$t('modals.addAccessPermissions.group.noData')"
      data-testid="add-access-restrictions-group-autocomplete"
      item-value="groupId"
      item-title="serviceName"
      clear-on-select
      v-model:search="groupSearchTerm"
      @update:search="onGroupsSearchUpdate($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :text="item.title"
          color="indigo-faint"
        >
          <div class="d-flex align-center">
            <coro-icon class="service-icon" :icon-name="item.raw.serviceName"></coro-icon>
            <span class="ml-1 mr-2">{{ item.raw.groupName }}</span>
          </div>
        </v-chip>
      </template>
      <template #item="{ props, item }">
        <v-list-item v-bind="props" title="">
          <div
            class="d-flex align-center"
            :data-testid="`add-access-restrictions-group-${item.raw.name}`"
          >
            <coro-icon class="service-icon" :icon-name="item.raw.serviceName"></coro-icon>
            <span class="ml-1">{{ item.raw.groupName }}</span>
          </div>
        </v-list-item>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-if="isSpecificUser"
      v-model="localValue.users"
      data-testid="add-user-modal-combobox"
      class="mt-3 coro-scrollable-combobox"
      :loading="dataLoading"
      :items="usersOptions"
      :label="$t('modals.addAccessPermissions.users.label')"
      :placeholder="$t('modals.addAccessPermissions.users.placeholder')"
      :rules="[atLeastOneIsRequired(), usersRule]"
      variant="outlined"
      multiple
      no-filter
      return-object
      hide-no-data
      item-value="userId"
      item-title="email"
      clear-on-select
      @update:model-value="handleChange()"
      @update:search="onUsersSearchUpdate($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :text="item.title"
          :color="getChipColor(item.raw.email, Patterns.EMAIL)"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="ml-1 mr-2">{{ item.raw.email }}</span>
          </div>
        </v-chip>
      </template>
      <template #item="{ props, item }">
        <v-list-item
          v-bind="props"
          :data-testid="`item-${camelCase(item.raw.email)}`"
          :title="item.raw.email"
        >
        </v-list-item>
      </template>
    </v-autocomplete>
    <v-autocomplete
      v-if="isSpecificLabel"
      v-model="localValue.labels"
      data-testid="add-access-restrictions-label-autocomplete"
      class="mb-3"
      :items="labelsOptions"
      :label="$t('general.name')"
      :placeholder="$t('general.name')"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      item-value="id"
      :no-data-text="$t('modals.addAccessPermissions.group.noData')"
      :loading="dataLoading"
      item-title="name"
      clear-on-select
      return-object
      multiple
      v-model:search="labelSearchTerm"
      @update:search="onLabelsSearchUpdate($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          size="default"
          :color="item.raw.color"
          density="compact"
          :text="item.raw.name"
          variant="flat"
          label
          rounded="3"
        >
          <span class="coro-label text-uppercase text-primary" v-text="item.raw.name" />
        </v-chip>
      </template>
      <template #item="{ item, props }">
        <v-list-item v-bind="props" title="">
          <div class="d-flex align-center label-popup-block">
            <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
            <span>
              {{ item.raw.name }}
            </span>
          </div>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import CoroIcon from "@/components/CoroIcon.vue";
import { AccessPermissionType, Service } from "@/constants/cloud-apps";
import type {
  AccessPermissionItem,
  Group,
  MonitoredScopeUser,
} from "@/_store/cloud-apps/access-permissions.module";
import type { UserLabel } from "@/_store/users-settings/user-labels.module";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import uniqBy from "lodash/uniqBy";
import { Patterns } from "@coronet/cui";
import { atLeastOneIsRequired } from "@/_helpers/validators";
import camelCase from "lodash/camelCase";
import { useI18n } from "vue-i18n";
import { useAccessPermissionsStore } from "@/_store/cloud-apps/access-permissions.module";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";

export default defineComponent({
  components: { CoroIcon, ButtonToggleGroup },
  props: {
    modelValue: {
      type: Object as PropType<AccessPermissionItem>,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const accessPermissionsStore = useAccessPermissionsStore();
    const dataLoading = ref(false);
    const groupSearchTerm = ref<string | undefined>("");
    const labelSearchTerm = ref<string | undefined>("");
    const localValue = ref<AccessPermissionItem>(cloneDeep(props.modelValue));
    const groupsOptions = ref<Group[]>(
      localValue.value.groups?.length ? [...localValue.value.groups] : []
    );
    const usersOptions = ref<MonitoredScopeUser[]>(
      localValue.value.users?.length ? [...localValue.value.users] : []
    );
    const labelsOptions = ref<UserLabel[]>(
      localValue.value.labels?.length ? [...localValue.value.labels] : []
    );

    watch(
      () => props.modelValue,
      (value) => {
        if (!isEqual(value, localValue.value)) {
          localValue.value = cloneDeep(value);
          groupsOptions.value = value.groups?.length ? [...value.groups] : [];
          usersOptions.value = value.users?.length ? [...value.users] : [];
          labelsOptions.value = value.labels?.length ? [...value.labels] : [];
        }
      },
      { deep: true }
    );

    watch(
      localValue,
      (value) => {
        if (!isEqual(value, props.modelValue)) {
          emit("update:modelValue", value);
        }
      },
      { deep: true }
    );

    const onUsersSearchUpdate = debounce(async function (email) {
      dataLoading.value = true;

      try {
        usersOptions.value = await accessPermissionsStore.searchProtectedUsers({
          email,
          serviceName: localValue.value.serviceName,
        });
      } catch (e) {
        console.error(e);
      }
      dataLoading.value = false;
    }, 500);

    const onLabelsSearchUpdate = debounce(async function (name) {
      labelsOptions.value = [...localValue.value.labels];

      if (!name) {
        return;
      }

      dataLoading.value = true;

      try {
        const responseData = await accessPermissionsStore.searchLabels(name);

        labelsOptions.value = uniqBy([...localValue.value.labels, ...responseData], "id");
      } catch (e) {
        console.error(e);
      }
      dataLoading.value = false;
    }, 500);

    const onGroupsSearchUpdate = debounce(async function (value) {
      dataLoading.value = true;

      try {
        const responseData = await accessPermissionsStore.searchGroups({
          searchTerm: value,
          limit: 10,
          service: localValue.value.serviceName,
        });
        groupsOptions.value = uniqBy([...localValue.value.groups, ...responseData], "groupId");
      } catch (e) {
        console.error(e);
      }

      dataLoading.value = false;
    }, 500);

    const userTypes: AccessPermissionType[] = [
      AccessPermissionType.ALL_USERS,
      AccessPermissionType.SPECIFIC_GROUPS,
      AccessPermissionType.SPECIFIC_USERS,
      AccessPermissionType.LABEL,
    ];

    const userTypeItems = computed<ButtonToggleItem[]>(() =>
      userTypes.map((type) => ({
        value: type,
        label: i18n.t(`modals.addAccessPermissions.assignUsers.userType.${type}`),
      }))
    );

    const isSpecificGroup = computed(() => {
      return localValue.value.userType === AccessPermissionType.SPECIFIC_GROUPS;
    });
    const isSpecificUser = computed(() => {
      return localValue.value.userType === AccessPermissionType.SPECIFIC_USERS;
    });
    const isSpecificLabel = computed(() => {
      return localValue.value.userType === AccessPermissionType.LABEL;
    });

    const getChipColor = (value: string, pattern: RegExp) => {
      const isValid = pattern.test(value);
      return isValid ? "indigo-faint" : "error";
    };

    const handleChange = () => {
      const normalizedUsers = (localValue.value.users as Array<string | MonitoredScopeUser>)
        .filter((u: string | MonitoredScopeUser) => !!u)
        .map((user: string | MonitoredScopeUser): MonitoredScopeUser => {
          if ((user as MonitoredScopeUser).email) {
            return user as MonitoredScopeUser;
          }
          return {
            email: user as string,
            service: Service.CORONET,
          };
        });
      localValue.value.users = normalizedUsers;
    };

    const usersRule = (values: MonitoredScopeUser[]) => {
      if (values) {
        if (values.length === 0) return true;

        for (const value of values) {
          if (!Patterns.EMAIL.test(value.email)) {
            return i18n.t("validations.email");
          }
        }
      }
      return true;
    };

    return {
      atLeastOneIsRequired,
      camelCase,
      dataLoading,
      getChipColor,
      groupSearchTerm,
      groupsOptions,
      handleChange,
      isSpecificGroup,
      isSpecificLabel,
      isSpecificUser,
      labelSearchTerm,
      labelsOptions,
      localValue,
      onGroupsSearchUpdate,
      onLabelsSearchUpdate,
      onUsersSearchUpdate,
      Patterns,
      usersOptions,
      usersRule,
      userTypeItems,
    };
  },
});
</script>

<style scoped lang="scss">
.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.service-icon {
  width: 24px;
  height: 24px;
}
</style>
